import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import type { Scene } from '@babylonjs/core/scene';

/** The atlas's three images (`tools/assets/arenaAtlas.ts`), under the client's public root. */
export const ARENA_TEXTURES = {
  color: '/textures/arena/color.webp',
  normal: '/textures/arena/normal.webp',
  gloss: '/textures/arena/gloss.webp',
} as const;

/**
 * One material for every chunk of an arena (`content:quality#2`, audit step 26b): the tile
 * atlas's colour, its normal map for the relief of stone, bark and cobbles, and its gloss for
 * where the ground shines (wet cobbles and ice) and where it is matte (grass and dirt). Materials,
 * not triangles, are what the headset can afford (doc 12). Trilinear sampling and anisotropy keep
 * the photographic tiles from shimmering at a distance; each cell's gutter keeps a far-off mip
 * level from mixing with its neighbour in the atlas.
 */
export function createArenaMaterial(scene: Scene): StandardMaterial {
  const material = new StandardMaterial('arenaTiles', scene);
  const texture = (url: string) => {
    const t = new Texture(url, scene, false, true, Texture.TRILINEAR_SAMPLINGMODE);
    t.anisotropicFilteringLevel = 4;
    return t;
  };
  material.diffuseTexture = texture(ARENA_TEXTURES.color);
  material.bumpTexture = texture(ARENA_TEXTURES.normal);
  material.specularTexture = texture(ARENA_TEXTURES.gloss);
  // The gloss image carries the highlight's colour, and its alpha how tight the highlight is.
  material.useGlossinessFromSpecularMapAlpha = true;
  material.specularColor = Color3.White();
  material.specularPower = 48;
  material.ambientColor = Color3.Black();
  return material;
}
